import { t, Trans } from '@lingui/macro';

import Alert from '@decisiv/ui-components/lib/components/Alert';
import Flex from '@decisiv/ui-components/lib/components/Flex';
import Tabs, { Tab } from '@decisiv/ui-components/lib/components/Tabs';
import { H3 } from '@decisiv/ui-components/lib/components/Typography';

import ValidVins from './ValidVins';
import InvalidVins from './InvalidVins';
import OwnedByAnotherFleet from './OwnedByAnotherFleet';
import OwnedByAnotherDepot from './OwnedByAnotherDepot';

import { Container, TabsContainer } from './styles';
import { useAddAssetContext } from '../context/AddAssetContext';

/**
 * Review step. VIN identification runs in the wizard's intermediate state (see
 * IdentifyingAssets) before this step is revealed, so here we only present the
 * categorized results already written to saved state. Assets already in the
 * current depot are intentionally not shown — they surface on Add Unit #.
 */
export default function StepTwo() {
  const { savedState } = useAddAssetContext();

  const { validVins, invalidVins, usedByOther, usedByOtherDepot, failed } =
    savedState.stepTwo;

  if (failed) {
    return (
      <Container>
        <H3 marginBottom={1}>
          <Trans>Review</Trans>
        </H3>
        <div>
          <Alert intent="danger" title={t`Asset identification failed`}>
            <Trans>
              We were unable to identify your assets at this time. Please try
              again or go back and adjust your asset list.
            </Trans>
          </Alert>
        </div>
      </Container>
    );
  }

  const hasRehomedAssets = usedByOther.length + usedByOtherDepot.length > 0;

  return (
    <Container>
      <H3 marginBottom={1}>
        <Trans>Review</Trans>
      </H3>
      <div>
        <Alert intent="information" title={t`Asset Validation & Eligibility`}>
          <Trans>
            Assets have been validated and categorized. Invalid VINs will be
            skipped.
          </Trans>
        </Alert>
      </div>
      {hasRehomedAssets && (
        <Flex marginTop={1}>
          <Alert intent="warning" title={t`Assets in other fleets or depots`}>
            <Trans>
              Assets that are in other fleets or depots will be automatically
              moved to the selected depot.
            </Trans>
          </Alert>
        </Flex>
      )}
      <TabsContainer>
        <Tabs defaultSelectedId="valid-vins" renderHiddenTabsContent={false}>
          <Tab id="valid-vins" title={t`New Assets (${validVins.length})`}>
            <ValidVins />
          </Tab>
          <Tab
            id="owned-by-another-fleet"
            title={t`In Other Accounts (${usedByOther.length})`}
          >
            <OwnedByAnotherFleet />
          </Tab>
          <Tab
            id="owned-by-another-depot"
            title={t`In Other Depots (${usedByOtherDepot.length})`}
          >
            <OwnedByAnotherDepot />
          </Tab>
          <Tab id="invalid-vins" title={t`Invalid VIN (${invalidVins.length})`}>
            <InvalidVins />
          </Tab>
        </Tabs>
      </TabsContainer>
    </Container>
  );
}
